import { useEffect, useCallback } from "react";
import {
  faPlus,
  faGear,
  faVideo,
  faClock,
  faHistory,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import ShortStatCard from "./ShortStatCard";
import Button from "../../common/Button";
import Loading from "../../common/Loading";
import MessageText from "../../common/MessageText";
import Page from "../../common/Page";
import { useDispatch, useSelector } from "react-redux";
import LanguageSelector from "../../common/LanguageSelector";
import SiteSelector from "../../common/SiteSelector";
import VideoListCard from "./VideoListCard";
import { AIVideoActionCreators } from "../../../store/actions/aiVideo";

const AIVideoHomepage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const siteVirgulId = useSelector(
    (state) => state.site?.selectedSite?.virgulId
  );
  const videoCreation = useSelector(
    (state) => state.site?.selectedSite?.videoCreation
  );

  const homepage = useSelector((state) => state.aiVideo?.homepage);
  const loading = homepage?.loading;
  const liveVideos = homepage?.liveVideos || [];
  const liveVideosTotal = homepage?.liveVideosTotal || 0;
  const pastVideos = homepage?.pastVideos || [];
  const pastVideosTotal = homepage?.pastVideosTotal || 0;

  const fetchVideos = useCallback(() => {
    if (!videoCreation || !siteVirgulId) {
      navigate("/");
      return;
    }

    dispatch(
      AIVideoActionCreators.getAIVideoHomepageSummary({ siteVirgulId })
    );
  }, [videoCreation, siteVirgulId, navigate, dispatch]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  if (loading) {
    return <Loading />;
  }

  return (
    <Page
      title={
        <div className="flex-col">
          <div className="sm:flex sm:items-center justify-between">
            <div className="flex items-center">
              <SiteSelector
                siteFilter={(site) =>
                  site.videoCreation && site.id !== "all-sites"
                }
              />
            </div>
            <div className="flex items-center gap-2">
              <Button
                className="ml-auto"
                onClick={() => navigate("/ai-video/new")}
                variant="primary"
                icon={faPlus}
              >
                {t("new video")}
              </Button>
              <Button
                className="ml-auto"
                onClick={() => navigate("/ai-video/settings")}
                variant="primary"
                icon={faGear}
              >
                {t("video creation settings")}
              </Button>
              <LanguageSelector />
            </div>
          </div>
          <h2 className="text-xl font-bold text-gray-700 tracking-tight leading-tight my-4">
            {t("video creator")}
          </h2>
          <p className="text-gray-500 text-sm">
            {t("video creator description")}
          </p>
        </div>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ShortStatCard
          title={t("total videos")}
          value={liveVideosTotal + pastVideosTotal}
          icon={faVideo}
        />
        <ShortStatCard
          title={t("live videos")}
          value={liveVideosTotal}
          icon={faClock}
          isPublished={true}
        />
        <ShortStatCard
          title={t("ready to publish videos")}
          value={pastVideosTotal}
          icon={faHistory}
          isPublished={false}
        />
      </div>
      <div>
        {!liveVideos.length && !pastVideos.length ? (
          <div
            className="flex flex-col items-center justify-center"
            style={{ minHeight: "500px" }}
          >
            <div>
              <MessageText
                message={t(
                  "there are no ai videos available for your account."
                )}
                textAlignment="text-center"
              />
            </div>
            <div className="flex items-center justify-center space-x-2">
              <Button
                className="mt-4"
                onClick={() => navigate("/ai-video/new")}
                variant="primary"
                icon={faPlus}
              >
                {t("new video")}
              </Button>
              <Button
                className="mt-4"
                onClick={() => navigate("/ai-video/settings")}
                variant="primary"
                icon={faGear}
              >
                {t("video creation settings")}
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-4 gap-4">
              <VideoListCard
                videos={liveVideos}
                title={t("live videos")}
                isPublished={true}
              />
              <VideoListCard
                videos={pastVideos}
                title={t("ready to publish videos")}
                isPublished={false}
              />
            </div>
          </>
        )}
      </div>
    </Page>
  );
};

export default AIVideoHomepage;
